import { useLocation, useNavigate } from 'react-router-dom';
import { useAppStore } from '../store/useAppStore';
import { useRequireSession } from '../hooks/useRouteGuards';
import {
  CONTEXT_GROUPS,
  CONTEXT_GROUP_ORDER,
  CONTEXT_GROUP_PATHS,
  type ContextGroupKey,
} from '../features/session/contextGroups';
import { AnswerOptionGroup } from '../components/ui/AnswerOptionGroup';
import { Button } from '../components/ui/Button';
import { ProgressBar } from '../components/navigation/ProgressBar';
import { trackEvent } from '../utils/posthogClient';

interface ContextPageProps {
  group: ContextGroupKey;
}

export function ContextPage({ group }: ContextPageProps) {
  const ready = useRequireSession();
  const navigate = useNavigate();
  const location = useLocation();
  const cameFromReview = (location.state as { from?: string } | null)?.from === 'review';
  const contextQuestions = useAppStore((state) => state.dataModel.contextQuestions);
  const context = useAppStore((state) => state.session?.context);
  const setContextAnswer = useAppStore((state) => state.setContextAnswer);
  const preparationStepEnabled = useAppStore(
    (state) => state.settings.featureFlags.preparationStep.enabled,
  );
  const reviewStepEnabled = useAppStore((state) => state.settings.featureFlags.reviewStep.enabled);
  const analyticsEnabled = useAppStore((state) => state.settings.featureFlags.analytics.enabled);

  if (!ready || !context) return null;

  const fieldIds = CONTEXT_GROUPS[group];
  const fields = fieldIds
    .map((id) => contextQuestions.find((question) => question.id === id))
    .filter((question): question is NonNullable<typeof question> => !!question);

  const groupIndex = CONTEXT_GROUP_ORDER.indexOf(group);
  const allAnswered = fieldIds.every((id) => context[id] !== undefined);

  function goToPrevious(): void {
    if (analyticsEnabled) trackEvent('context_group_back', { group });
    if (groupIndex === 0) {
      void navigate(preparationStepEnabled ? '/vorbereitung' : '/');
    } else {
      const previous = CONTEXT_GROUP_ORDER[groupIndex - 1];
      if (previous) void navigate(CONTEXT_GROUP_PATHS[previous]);
    }
  }

  function goToNext(): void {
    if (analyticsEnabled) {
      const answers = Object.fromEntries(fieldIds.map((id) => [id, context?.[id]]));
      trackEvent('context_group_completed', { group, answers });
    }
    if (cameFromReview) {
      void navigate(reviewStepEnabled ? '/pruefen' : '/ergebnis');
      return;
    }
    const next = CONTEXT_GROUP_ORDER[groupIndex + 1];
    if (next) {
      void navigate(CONTEXT_GROUP_PATHS[next]);
    } else {
      void navigate('/test/1');
    }
  }

  return (
    <div className="page-container stack">
      <ProgressBar
        current={groupIndex + 1}
        total={CONTEXT_GROUP_ORDER.length}
        label={`Kontext – Schritt ${groupIndex + 1} von ${CONTEXT_GROUP_ORDER.length}`}
      />
      <h1>Kurze Fragen zu deinem Angebot</h1>

      <div className="stack">
        {fields.map((field) => (
          <section key={field.id} className="card stack">
            <h2>{field.question}</h2>
            <AnswerOptionGroup
              legend={field.question}
              name={field.id}
              options={field.options.map((option) => ({ id: option.value, label: option.label }))}
              selectedId={context[field.id]}
              onSelect={(value) => setContextAnswer(field.id, value)}
            />
          </section>
        ))}
      </div>

      <div className="stack" style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
        <Button variant="secondary" onClick={goToPrevious}>
          Zurück
        </Button>
        <Button onClick={goToNext} disabled={!allAnswered}>
          Weiter
        </Button>
      </div>
    </div>
  );
}
